import { useEffect, useState } from 'react';
import { api } from '../api/client';

const DOC_TYPES = ['Contrat', 'Lettre officielle', 'Certificat', 'Attestation', 'Convocation', 'Rapport', 'Circulaire', 'Règlement', 'Autre'];
const STATUSES = ['Actif', 'Archivé', 'Brouillon'];
const STATUS_COLOR = { 'Actif': 'green', 'Archivé': 'gray', 'Brouillon': 'navy' };

const EMPTY = { name: '', docType: 'Lettre officielle', description: '', content: '', status: 'Actif', version: '1.0', tags: '' };

export default function ModelsDocuments() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');

  function load() {
    api.get('/registry/documentTemplates').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
  }

  useEffect(load, []);

  function printTemplate(r) {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${r.name}</title>
    <style>body{font-family:'Times New Roman',serif;max-width:700px;margin:40px auto;line-height:1.8;}
    .header{text-align:center;border-bottom:2px solid #000;padding-bottom:12px;margin-bottom:20px;}
    @media print{button{display:none}}</style></head><body>
    <div class="header"><h2>Groupe Scolaire La Providence</h2><p>Année scolaire 2025-2026</p></div>
    <h3 style="text-align:center;text-decoration:underline;">${r.name}</h3>
    <div style="white-space:pre-wrap;">${r.content || ''}</div>
    <script>window.addEventListener('load',()=>window.print());</script></body></html>`);
    w.document.close();
  }

  const filtered = rows.filter((r) => !typeFilter || r.docType === typeFilter);

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Modèles de documents</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 180 }}>
            <option value="">Tous les types</option>
            {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Nouveau modèle</button>
        </div>
      </div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {filtered.length === 0 ? <div className="empty">Aucun modèle de document.</div> : (
          <div className="table-wrap"><table>
            <thead>
              <tr><th>Nom</th><th>Type</th><th>Version</th><th>Description</th><th>Statut</th><th></th></tr>
            </thead>
            <tbody>{filtered.map((r) => (
              <tr key={r.id}>
                <td><b>{r.name}</b></td>
                <td>{r.docType}</td>
                <td className="mono">{r.version || '1.0'}</td>
                <td style={{ maxWidth: 200 }}>{r.description || '—'}</td>
                <td><span className={`badge ${STATUS_COLOR[r.status] || 'gray'}`}>{r.status}</span></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Aperçu</button>
                  <button className="btn-ghost btn-sm" onClick={() => printTemplate(r)}>🖨</button>
                  <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <DocModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <DocView item={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} onPrint={() => printTemplate(view)} />}
    </div>
  );
}

function DocView({ item, onClose, onEdit, onPrint }) {
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{item.name}</h2>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, fontSize: '0.85rem', color: 'var(--muted)' }}>
          <span>{item.docType}</span><span>·</span><span>v{item.version}</span><span>·</span>
          <span className={`badge ${STATUS_COLOR[item.status] || 'gray'}`}>{item.status}</span>
        </div>
        {item.description && <p style={{ marginBottom: 12, color: 'var(--muted)' }}>{item.description}</p>}
        <div style={{ background: 'var(--surface-soft, #f8f9fa)', border: '1px solid var(--line)', borderRadius: 6, padding: 16, whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', lineHeight: 1.8, minHeight: 200 }}>
          {item.content || <span style={{ color: 'var(--muted)' }}>Aucun contenu.</span>}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onPrint} style={{ width: 'auto', padding: '0 22px' }}>🖨 Imprimer</button>
          <button className="btn-ghost" onClick={onEdit}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function DocModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.name) { setError('Le nom est obligatoire.'); return; }
    setSaving(true);
    try {
      const payload = { data: f, label: f.name, searchText: `${f.name} ${f.docType} ${f.tags || ''}` };
      if (isEdit) await api.patch(`/registry/documentTemplates/${initial.id}`, payload);
      else await api.post('/registry/documentTemplates', payload);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier le modèle' : 'Nouveau modèle de document'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="grid-3">
          <div className="field"><label>Nom *</label><input value={f.name} onChange={set('name')} /></div>
          <div className="field"><label>Type</label>
            <select value={f.docType} onChange={set('docType')}>
              {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Version</label><input value={f.version} onChange={set('version')} placeholder="1.0" /></div>
          <div className="field"><label>Statut</label>
            <select value={f.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Tags</label><input value={f.tags} onChange={set('tags')} placeholder="Ex: rh, finance" /></div>
        </div>
        <div className="field"><label>Description</label><textarea value={f.description} onChange={set('description')} rows={2} /></div>
        <div className="field"><label>Contenu du modèle</label>
          <textarea value={f.content} onChange={set('content')} rows={12}
            style={{ fontFamily: 'Georgia, serif', lineHeight: 1.8 }}
            placeholder="Rédigez le contenu du modèle ici. Utilisez [ÉLÈVE], [CLASSE], [DATE], [DIRECTEUR] comme variables..." />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
